import { useEffect, useId, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiRequestError, apiRequest } from '../lib/api-client';
import { t } from '../lib/i18n';
import { AuthLayout } from './AuthLayout';
import { useAuth } from './auth-store';
import { ArrowIcon, CheckIcon, EyeIcon, LinkIcon, LockIcon, MailIcon } from './icons';
import { discoverSso, startSso, type SignInOption } from '../settings/sso-api';
import { ssoErrorMessage } from './sso-errors';

/**
 * Sign in, with a password or with a link sent by e-mail.
 *
 * Both doors lead to the same session, so they live on the same screen rather than behind a second
 * route: the address is typed once and the choice is which proof to give. The link is the way in for
 * somebody who never set a password they remember, which is most people most of the time.
 *
 * A third door appears on its own. When the address belongs to a company that has configured single
 * sign-on, the button for it is offered as soon as the address is typed — home-realm discovery,
 * which is what stops an employee of that company typing a password this product should never have
 * been given. It is offered, not forced: somebody with both still chooses.
 */

type Mode = 'password' | 'magic_link';

export function LoginPage(): React.JSX.Element {
  const { login } = useAuth();
  const navigate = useNavigate();
  const emailId = useId();
  const passwordId = useId();

  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [linkSentTo, setLinkSentTo] = useState<string | null>(null);

  /** Which company connection, if any, serves the address being typed. */
  const [ssoOptions, setSsoOptions] = useState<SignInOption[]>([]);
  const [redirecting, setRedirecting] = useState(false);

  // Where a failed callback lands, carrying a code rather than a sentence.
  const [params] = useSearchParams();
  const [ssoError, setSsoError] = useState<string | null>(() =>
    ssoErrorMessage(params.get('sso_erro')),
  );

  /*
   * Discovery, a moment after typing stops.
   *
   * Debounced rather than on blur: somebody who types their address and goes straight for the
   * password field never blurs it, and the button would appear after they had already decided.
   */
  useEffect(() => {
    const address = email.trim();

    if (!address.includes('@')) {
      setSsoOptions([]);

      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      discoverSso(address)
        .then((found) => {
          // Defensive about the shape as well as the failure: this runs on the sign-in screen,
          // which must not be the one page in the product that can fail to render.
          if (!cancelled) setSsoOptions(Array.isArray(found?.options) ? found.options : []);
        })
        // A discovery that fails is a button that is not offered, never an error on the screen:
        // the password field beside it still works.
        .catch(() => {
          if (!cancelled) setSsoOptions([]);
        });
    }, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [email]);

  async function enterWithSso(connectionId: string): Promise<void> {
    setRedirecting(true);
    setSsoError(null);

    try {
      const started = await startSso(connectionId);
      window.location.assign(started.authorization_url);
    } catch {
      setSsoError(t('sso.startFailed'));
      setRedirecting(false);
    }
  }

  function switchTo(next: Mode): void {
    setMode(next);
    setError(null);
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      if (mode === 'magic_link') {
        await apiRequest('/v1/auth/magic-link', { method: 'POST', body: { email } });
        setLinkSentTo(email);
      } else {
        await login(email, password);
        navigate('/');
      }
    } catch (caught) {
      // The API answers the same way for a wrong password and an unknown address; the UI keeps that
      // property rather than guessing which one it was.
      setError(
        caught instanceof ApiRequestError && caught.code === 'auth.invalid_credentials'
          ? t('login.invalidCredentials')
          : t('error.generic'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (linkSentTo) {
    return (
      <AuthLayout>
        <div className="auth__done">
          <span className="auth__done-mark" aria-hidden="true">
            <CheckIcon />
          </span>

          <h1>{t('magic.sentTitle')}</h1>
          {/* Worded so it says nothing about whether the address is registered. */}
          <p>{t('magic.sentBody').replace('{email}', linkSentTo)}</p>
          <p className="auth__fineprint">{t('magic.sentFineprint')}</p>

          <button
            type="button"
            className="button button--primary"
            onClick={() => {
              setLinkSentTo(null);
              setError(null);
            }}
          >
            {t('magic.useAnother')}
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <header className="auth__heading">
        <h1>{t('login.title')}</h1>
        <p>{mode === 'password' ? t('login.subtitle') : t('magic.subtitle')}</p>
      </header>

      {/* A two-way switch, not a link: nothing typed so far is lost by changing your mind. */}
      <div className="segmented" role="tablist" aria-label={t('login.methodLabel')}>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'password'}
          className="segmented__option"
          onClick={() => switchTo('password')}
        >
          {t('login.withPassword')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'magic_link'}
          className="segmented__option"
          onClick={() => switchTo('magic_link')}
        >
          {t('login.withLink')}
        </button>
      </div>

      {ssoError && (
        <p className="alert" role="alert">
          {ssoError}
        </p>
      )}

      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <div className="input">
          <label htmlFor={emailId}>{t('field.email')}</label>
          <div className="input__control">
            <MailIcon />
            <input
              id={emailId}
              type="email"
              name="email"
              autoComplete="email"
              autoFocus
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
        </div>

        {/* Offered as soon as the address says which company it is, above the password it replaces. */}
        {ssoOptions.length > 0 && (
          <div className="auth__sso">
            {ssoOptions.map((option) => (
              <button
                key={option.connection_id}
                type="button"
                className="button button--quiet"
                disabled={redirecting}
                onClick={() => void enterWithSso(option.connection_id)}
              >
                {t('sso.enterWith').replace('{label}', option.label)}
              </button>
            ))}
            <p className="input__hint">{t('sso.orUseYourPassword')}</p>
          </div>
        )}

        {mode === 'password' && (
          <div className="input">
            <label htmlFor={passwordId}>{t('field.password')}</label>
            <div className="input__control">
              <LockIcon />
              <input
                id={passwordId}
                type={revealed ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <button
                type="button"
                className="input__reveal"
                onClick={() => setRevealed((current) => !current)}
                aria-label={revealed ? t('login.hidePassword') : t('login.showPassword')}
                aria-pressed={revealed}
              >
                <EyeIcon crossed={revealed} />
              </button>
            </div>
            {/* Under the field, not beside the label: the label floats on the border. */}
            <p className="input__aside">
              <Link className="input__aside-link" to="/esqueci-senha">
                {t('login.forgotPassword')}
              </Link>
            </p>
          </div>
        )}

        {mode === 'magic_link' && <p className="input__hint">{t('magic.hint')}</p>}

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <button type="submit" className="button button--primary" disabled={submitting}>
          {submitting
            ? mode === 'password'
              ? t('login.submitting')
              : t('magic.submitting')
            : mode === 'password'
              ? t('login.submit')
              : t('magic.submit')}
          {!submitting && (mode === 'password' ? <ArrowIcon /> : <LinkIcon />)}
        </button>
      </form>

      <p className="auth__switch">
        {t('login.noAccount')} <Link to="/cadastro">{t('login.signupLink')}</Link>
      </p>
    </AuthLayout>
  );
}
